import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./AuthGate.css";
import { validatePassword, PasswordRules } from "./utils/passwordRules";
import PasswordInput from "./components/PasswordInput";

const _gwConfig = (typeof window !== "undefined" && window.CS_GATEWAY_CONFIG) || {};
const CORE_URL = _gwConfig.CORE_URL || process.env.REACT_APP_CORE_URL || "http://localhost:8090";
const STUDIO_URL = process.env.REACT_APP_STUDIO_URL || "";

const authFetch = (path, options = {}) =>
  fetch(`${CORE_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
  });

const getSafeRedirectTarget = () => {
  if (typeof window === "undefined") return "";

  const redirect = new URLSearchParams(window.location.search).get("redirect");
  if (!redirect) return "";

  let target;
  try {
    target = new URL(redirect, window.location.href);
  } catch {
    return "";
  }

  const allowedOrigins = new Set([window.location.origin]);
  if (STUDIO_URL) {
    try {
      allowedOrigins.add(new URL(STUDIO_URL, window.location.href).origin);
    } catch {
      // Ignore malformed optional studio URL config
    }
  }

  if (!allowedOrigins.has(target.origin)) return "";

  const current = new URL(window.location.href);
  if (
    target.origin === current.origin &&
    target.pathname === current.pathname &&
    target.search === current.search
  ) {
    return "";
  }

  return target.toString();
};

const getInviteToken = () => {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/^(?:\/gateway)?\/invite\/([a-f0-9]+)\/?$/i);
  return match ? match[1] : null;
};

export default function AuthGate({ children }) {
  const [state, setState] = useState("loading");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [authMode, setAuthMode] = useState("internal");
  const [inviteInfo, setInviteInfo] = useState(null); // { email, role } from validated token
  const [signupMode, setSignupMode] = useState(false);
  const redirectTarget = useMemo(getSafeRedirectTarget, []);
  const inviteToken = useMemo(getInviteToken, []);
  const checkStatus = useCallback(async () => {
    // Fetch auth status first — needed for auth mode and invite flow.
    let statusData = {};
    let statusOk = false;
    try {
      const statusRes = await authFetch("/auth/status");
      if (statusRes.ok) {
        statusData = await statusRes.json();
        statusOk = true;
        setAuthMode(statusData.auth_mode || "internal");
      } else {
        setState("error");
        setError("Could not connect to server. Please try again.");
        return;
      }
    } catch {
      setState("error");
      setError("Could not connect to server. Please try again.");
      return;
    }

    // Invite URLs always show the invite flow, even if already logged in.
    // The user needs to see the invite context and accept it.
    if (inviteToken) {
      try {
        const res = await authFetch(`/auth/invite/${inviteToken}`);
        if (res.ok) {
          const data = await res.json();
          setInviteInfo(data);
          setSignupMode(true);
          setState("invite");
        } else {
          setState("invite-invalid");
        }
      } catch {
        setState("invite-invalid");
      }
      return;
    }

    // Check session — if already logged in, go straight to app.
    try {
      const meRes = await authFetch("/auth/me");
      if (meRes.ok) {
        // Seed the theme localStorage + <html data-theme> from the server
        // before children render, so GatewayTopbar's getInitialTheme reads
        // the authoritative value on first mount (no dark→light flash).
        try {
          const me = await meRes.json();
          const theme = me?.preferences?.theme;
          if (theme === "light" || theme === "dark") {
            try { window.localStorage.setItem("gw-theme", theme); } catch {}
            document.documentElement.setAttribute("data-theme", theme);
            // OpenBridge components (charts/gauges) theme via their own attr.
            // bright/dusk, not day/night — see shared.js theme effect.
            document.documentElement.setAttribute("data-obc-theme", theme === "light" ? "bright" : "dusk");
          }
        } catch {}
        setState("ready");
        return;
      }
    } catch {
      // Not logged in or unreachable — continue to login UI.
    }

    // Not authenticated — show appropriate login UI.
    if (!statusOk) {
      setError("Could not connect to server");
      setState("error");
      return;
    }
    if (!statusData.bootstrapped && statusData.auth_mode !== "oidc") {
      setState("bootstrap");
      return;
    }
    setState("login");

    // Pick up error messages from OIDC callback redirects (e.g. email conflict).
    const params = new URLSearchParams(window.location.search);
    const callbackError = params.get("error");
    if (callbackError) {
      setError(callbackError);
      // Clean the URL so the error doesn't persist on refresh.
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [inviteToken]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (state !== "ready" || !redirectTarget) return;
    window.location.replace(redirectTarget);
  }, [state, redirectTarget]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (state === "bootstrap" && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);

    try {
      const endpoint = state === "bootstrap" ? "/auth/bootstrap" : "/auth/login";
      const res = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const text = await res.text();
        setError(text.trim() || "Request failed");
        setSubmitting(false);
        return;
      }

      // Bootstrap now auto-logins on the server side (sets session cookie).
      // No separate login call needed.

      setUsername("");
      setPassword("");
      setConfirmPassword("");
      setState("ready");
    } catch {
      setError("Could not connect to server");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch("/auth/accept-invite", {
        method: "POST",
        body: JSON.stringify({
          token: inviteToken,
          username,
          password,
          confirm_password: confirmPassword,
          first_name: firstName,
          last_name: lastName,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        setError(text.trim() || "Failed to accept invite");
        setSubmitting(false);
        return;
      }
      // Accept-invite sets the session cookie automatically — redirect to original target or editor
      window.location.replace(redirectTarget || "/editor");
    } catch {
      setError("Could not connect to server");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCognitoLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const body = { email, password };
      if (inviteToken) body.invite_token = inviteToken;
      if (redirectTarget) body.redirect = redirectTarget;
      const res = await authFetch("/auth/cognito-login", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        setError(text.trim() || "Sign in failed");
        setSubmitting(false);
        return;
      }
      const data = await res.json();
      window.location.replace(data.redirect || redirectTarget || "/editor");
    } catch {
      setError("Could not connect to server");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCognitoSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        email: inviteInfo?.email || email,
        password,
        invite_token: inviteToken,
      };
      if (redirectTarget) body.redirect = redirectTarget;
      const res = await authFetch("/auth/cognito-signup", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        if (res.status === 409) {
          setError("An account with this email already exists. Try signing in instead.");
          setSignupMode(false);
          setPassword("");
          setConfirmPassword("");
        } else {
          setError(text.trim() || "Sign up failed");
        }
        setSubmitting(false);
        return;
      }
      const data = await res.json();
      window.location.replace(data.redirect || redirectTarget || "/editor");
    } catch {
      setError("Could not connect to server");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const params = new URLSearchParams();
    params.set("identity_provider", "GoogleOIDC");
    params.set("prompt", "select_account");
    if (redirectTarget) params.set("redirect", redirectTarget);
    if (inviteToken) params.set("invite", inviteToken);
    window.location.href = `${CORE_URL}/auth/oidc/login?${params.toString()}`;
  };

  if (state === "loading") {
    return (
      <div className="authGate">
        <div className="authCard">
          <div className="authLoading">Connecting...</div>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="authGate">
        <div className="authCard" style={{ textAlign: "center" }}>
          <img src="/logo-white.svg" alt="Control Seat" className="authLogo" />
          <h1 className="authTitle">Connection Error</h1>
          <p className="authSubtitle" style={{ marginBottom: 20 }}>
            {error || "Could not connect to server. Please try again."}
          </p>
          <button className="authButton" onClick={() => { setError(""); setState("loading"); checkStatus(); }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (state === "ready" && redirectTarget) {
    return (
      <div className="authGate">
        <div className="authCard">
          <div className="authLoading">Redirecting...</div>
        </div>
      </div>
    );
  }

  if (state === "ready") {
    return children;
  }

  if (state === "invite-invalid") {
    return (
      <div className="authGate">
        <div className="authCard" style={{ textAlign: "center" }}>
          <img src="/logo-white.svg" alt="Control Seat" className="authLogo" />
          <h1 className="authTitle">Invite Link Invalid</h1>
          <p className="authSubtitle" style={{ marginBottom: 20 }}>
            This invite link has expired, been revoked, or was already used.
          </p>
          <button className="authButton" onClick={() => { window.location.replace("/gateway"); }}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (state === "invite") {
    // In OIDC mode, the main login form (below) handles invites —
    // it shows the invite context in the subtitle and includes the invite token in the POST.
    if (authMode !== "oidc") return (
      <div className="authGate">
        <div className="authCard" style={{ width: 420 }}>
          <img src="/logo-white.svg" alt="Control Seat" className="authLogo" />
          <h1 className="authTitle">Accept Invitation</h1>
          <p className="authSubtitle">
            {inviteInfo ? `You've been invited as ${inviteInfo.role} for ${inviteInfo.email}` : "Create your account to get started"}
          </p>
          <form onSubmit={handleAcceptInvite} className="authForm">
            <div style={{ display: "flex", gap: 12 }}>
              <div className="gwField" style={{ flex: 1 }}>
                <label className="gwLabel">First Name</label>
                <input type="text" placeholder="First name" value={firstName}
                  onChange={(e) => setFirstName(e.target.value)} className="gwInput" />
              </div>
              <div className="gwField" style={{ flex: 1 }}>
                <label className="gwLabel">Last Name</label>
                <input type="text" placeholder="Last name" value={lastName}
                  onChange={(e) => setLastName(e.target.value)} className="gwInput" />
              </div>
            </div>
            <div className="gwField">
              <label className="gwLabel">Username</label>
              <input type="text" placeholder="Choose a username" value={username}
                onChange={(e) => setUsername(e.target.value)} className="gwInput"
                autoFocus autoComplete="username" />
            </div>
            <div className="gwField">
              <label className="gwLabel">Password</label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a password" autoComplete="new-password" />
              <PasswordRules password={password} />
            </div>
            <div className="gwField">
              <label className="gwLabel">Confirm Password</label>
              <PasswordInput value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password" autoComplete="new-password" />
            </div>
            {error && <div className="authError">{error}</div>}
            <button type="submit" className="authButton"
              disabled={submitting || !username || !password || !confirmPassword || !validatePassword(password)}>
              {submitting ? "..." : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (authMode === "oidc") {
    const isInvite = state === "invite" && inviteInfo;

    return (
      <div className="authGate">
        <div className="authCard">
          <img src="/logo-white.svg" alt="Control Seat" className="authLogo" />
          <h1 className="authTitle">
            {isInvite && signupMode ? "Create Account" : "Sign in"}
          </h1>
          <p className="authSubtitle">
            {isInvite
              ? `You've been invited as ${inviteInfo.role} for ${inviteInfo.email}`
              : "Welcome back. Sign in to your account."}
          </p>

          <button type="button" className="authButtonGoogle" onClick={handleGoogleLogin} disabled={submitting}>
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="authDivider"><span>or</span></div>

          {isInvite && signupMode ? (
            <form onSubmit={handleCognitoSignup} className="authForm">
              <div className="gwField">
                <label className="gwLabel">Email address</label>
                <input
                  type="email"
                  value={inviteInfo.email}
                  className="gwInput"
                  readOnly
                  disabled
                />
              </div>
              <div className="gwField">
                <label className="gwLabel">Password</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choose a password"
                  autoComplete="new-password"
                  autoFocus
                />
              </div>
              <div className="gwField">
                <label className="gwLabel">Confirm Password</label>
                <PasswordInput
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                />
              </div>
              {error && <div className="authError">{error}</div>}
              <button
                type="submit"
                className="authButton"
                disabled={submitting || !password || !confirmPassword || password !== confirmPassword}
              >
                {submitting ? "..." : "Create Account"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleCognitoLogin} className="authForm">
              <div className="gwField">
                <label className="gwLabel">Email address</label>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="gwInput"
                  autoFocus
                  autoComplete="email"
                />
              </div>
              <div className="gwField">
                <label className="gwLabel">Password</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  autoComplete="current-password"
                />
              </div>
              {error && <div className="authError">{error}</div>}
              <button
                type="submit"
                className="authButton"
                disabled={submitting || !email || !password}
              >
                {submitting ? "..." : "Sign in"}
              </button>
            </form>
          )}

          {isInvite && (
            <p style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "var(--gw-muted)" }}>
              {signupMode ? (
                <>Already have an account?{" "}
                  <button type="button" onClick={() => { setSignupMode(false); setPassword(""); setConfirmPassword(""); setError(""); }}
                    style={{ background: "none", border: "none", color: "var(--gw-text)", cursor: "pointer", textDecoration: "underline", font: "inherit" }}>
                    Sign in
                  </button>
                </>
              ) : (
                <>Don't have an account?{" "}
                  <button type="button" onClick={() => { setSignupMode(true); setPassword(""); setConfirmPassword(""); setError(""); }}
                    style={{ background: "none", border: "none", color: "var(--gw-text)", cursor: "pointer", textDecoration: "underline", font: "inherit" }}>
                    Create one
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  const isBootstrap = state === "bootstrap";

  return (
    <div className="authGate">
      <div className="authCard">
        <img src="/logo-white.svg" alt="Control Seat" className="authLogo" />
        <h1 className="authTitle">Control Seat Login</h1>
        <p className="authSubtitle">
          {isBootstrap
            ? "Create your admin account to get started"
            : "Sign in to continue"}
        </p>
        <form onSubmit={handleSubmit} className="authForm">
          <div className="gwField">
            <label className="gwLabel">Username</label>
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="gwInput"
              autoFocus
              autoComplete="username"
            />
          </div>
          <div className="gwField">
            <label className="gwLabel">Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete={isBootstrap ? "new-password" : "current-password"}
            />
            {isBootstrap && <PasswordRules password={password} />}
          </div>
          {isBootstrap && (
            <div className="gwField">
              <label className="gwLabel">Confirm Password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
            </div>
          )}
          {error && <div className="authError">{error}</div>}
          <button
            type="submit"
            className="authButton"
            disabled={submitting || !username || !password || (isBootstrap && (!confirmPassword || !validatePassword(password)))}
          >
            {submitting
              ? "..."
              : isBootstrap
                ? "Create Admin Account"
                : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
